import { useState } from 'react'
import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useNavigate } from '@tanstack/react-router'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { PlusIcon } from '@gto/icons-react/outline/plus'
import { Button } from '@gto/ui/button'
import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { StatusBadge } from '@/components/status-badge'
import { fluidColumn } from '@/components/table-columns'
import { TableFilterBar } from '@/components/table-filter-bar'
import { methodLabel } from '@/domain/payout-methods'
import { useReportQueryError } from '@/hooks/use-report-query-error'

import { type SettlementListItem, useSettlementList, useSettlementOverview } from './api'
import { PROVIDER_LABELS } from './labels'

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_OPTION_KEYS = [
  { value: 'ALL', labelId: 'admin.settlements.status.all' },
  { value: 'DRAFT', labelId: 'admin.settlements.status.draft' },
  { value: 'READY', labelId: 'admin.settlements.status.ready' },
  { value: 'PROCESSING', labelId: 'admin.settlements.status.processing' },
  { value: 'COMPLETED', labelId: 'admin.settlements.status.completed' },
  { value: 'FAILED', labelId: 'admin.settlements.status.failed' },
  { value: 'PARTIAL', labelId: 'admin.settlements.status.partial' },
] as const

// ─── Settlement Columns ─────────────────────────────────────────────────────

const useSettlementColumns = (): ColumnDef<SettlementListItem>[] => {
  const { formatMessage } = useIntl()

  return createColumns<SettlementListItem>([
    {
      accessorKey: 'settlement_number',
      header: formatMessage({ id: 'admin.settlements.number' }),
      ...fluidColumn({ min: 90, flex: 0.5 }),
      cell: ({ getValue }) => `SET-${String(getValue()).padStart(4, '0')}`,
    },
    {
      accessorKey: 'provider',
      header: formatMessage({ id: 'admin.settlements.provider' }),
      ...fluidColumn({ min: 80, flex: 0.6 }),
      cell: ({ getValue }) => PROVIDER_LABELS[String(getValue())] ?? String(getValue()),
    },
    {
      accessorKey: 'total_amount',
      header: formatMessage({ id: 'admin.settlements.totalAmount' }),
      ...fluidColumn({ min: 100, flex: 0.8, align: 'right' }),
      cell: ({ row }) => (
        <FormattedNumber
          currency={row.original.currency}
          style="currency"
          value={row.original.total_amount}
        />
      ),
    },
    {
      accessorKey: 'payout_count',
      header: formatMessage({ id: 'admin.settlements.payoutCount' }),
      ...fluidColumn({ min: 80, flex: 0.5, align: 'right' }),
    },
    {
      accessorKey: 'created_at',
      header: formatMessage({ id: 'admin.settlements.created' }),
      ...fluidColumn({ min: 120, flex: 0.8 }),
      // App intl, not the browser's locale — matches the settlement builder's cell.
      cell: ({ getValue }) => (
        <FormattedDate
          day="numeric"
          month="short"
          value={String(getValue())}
          year="numeric"
        />
      ),
    },
    {
      accessorKey: 'created_by_email',
      header: formatMessage({ id: 'admin.settlements.createdBy' }),
      ...fluidColumn({ min: 120, flex: 0.8 }),
      cell: ({ getValue }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          {getValue() == null ? '—' : String(getValue())}
        </Typography>
      ),
    },
    {
      accessorKey: 'status',
      header: formatMessage({ id: 'admin.settlements.status' }),
      ...fluidColumn({ min: 90, flex: 0.6, align: 'right' }),
      cell: ({ getValue }) => <StatusBadge status={String(getValue())} />,
    },
  ])
}

// ─── Main Page ──────────────────────────────────────────────────────────────

export const SettlementsPage = () => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const [statusFilter, setStatusFilter] = useState('ALL')

  const overviewQuery = useSettlementOverview()
  const {
    data: overview,
    isLoading: overviewLoading,
    isError: overviewError,
    refetch: refetchOverview,
  } = overviewQuery

  const activeStatus = statusFilter === 'ALL' ? undefined : statusFilter
  const listQuery = useSettlementList(
    activeStatus === undefined ? undefined : { status: activeStatus },
  )
  const {
    data: settlements,
    isLoading: listLoading,
    isError: listError,
    refetch: refetchList,
  } = listQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  // Reports on TERMINAL failure so retries don't multiply Sentry events.
  useReportQueryError(overviewQuery, 'useSettlementOverview failed: ')
  useReportQueryError(listQuery, 'useSettlementList failed: ')

  const columns = useSettlementColumns()

  const statusOptions = STATUS_OPTION_KEYS.map((opt) => ({
    value: opt.value,
    label: formatMessage({ id: opt.labelId }),
  }))

  const totalPending = overview?.reduce((sum, ch) => sum + ch.count, 0) ?? 0
  const totalPendingAmount = overview?.reduce((sum, ch) => sum + ch.total_amount, 0) ?? 0

  // A failed overview drives the create CTA and the KPI strip; a failed list
  // drives the table. Either failure must surface inline + reach Sentry instead
  // of rendering a fake zeroed page.
  const hasError = overviewError || listError

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="admin.settlements.title" />}
        actions={
          <Button
            disabled={totalPending === 0 || overviewError}
            variant="contained"
            startIcon={
              <PlusIcon
                height={18}
                width={18}
              />
            }
            onClick={() => void navigate({ to: '/admin/settlements/new' })}
          >
            <FormattedMessage id="admin.settlements.create" />
          </Button>
        }
      />

      <Page.Content>
        {hasError ? (
          <InlineQueryError
            data-tst="settlements-error"
            messageId="admin.settlements.error"
            onRetry={() => {
              if (overviewError) {
                void refetchOverview()
              }
              if (listError) {
                void refetchList()
              }
            }}
          />
        ) : (
          <>
            {/* Channel Overview KPIs */}
            <div
              className="flex flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<BanknotesIcon />}
                label={<FormattedMessage id="admin.settlements.pendingPayouts" />}
                loading={overviewLoading}
                tooltip={formatMessage({ id: 'tooltip.settlements.pendingPayouts' })}
                value={<FormattedNumber value={totalPending} />}
              />
              <DashboardKpiTile
                icon={<CurrencyDollarIcon />}
                label={<FormattedMessage id="admin.settlements.pendingAmount" />}
                loading={overviewLoading}
                tooltip={formatMessage({ id: 'tooltip.settlements.pendingAmount' })}
                value={
                  <FormattedNumber
                    currency="USD"
                    style="currency"
                    value={totalPendingAmount}
                  />
                }
              />
              {overview
                ?.filter((ch) => ch.count > 0)
                .map((ch) => (
                  <DashboardKpiTile
                    key={ch.payout_method}
                    icon={<CurrencyDollarIcon />}
                    label={methodLabel(ch.payout_method)}
                    loading={overviewLoading}
                    value={
                      <span className="flex items-baseline gap-2">
                        <FormattedNumber value={ch.count} />
                        <span className="text-text-secondary">·</span>
                        <FormattedNumber
                          currency="USD"
                          style="currency"
                          value={ch.total_amount}
                        />
                      </span>
                    }
                  />
                ))}
            </div>

            {/* Filter Bar */}
            <TableFilterBar
              options={statusOptions}
              value={statusFilter}
              onValueChange={setStatusFilter}
            />

            {/* Settlements Table */}
            <StatsCard
              icon={<BanknotesIcon />}
              title={<FormattedMessage id="admin.settlements.listTitle" />}
            >
              {!listLoading && (settlements == null || settlements.length === 0) ? (
                <EmptyState title={<FormattedMessage id="admin.settlements.empty" />} />
              ) : (
                <DataTable
                  columns={columns}
                  data={settlements ?? []}
                  getRowId={(row) => row.id}
                  loading={listLoading}
                  onRowClick={(row) =>
                    void navigate({
                      to: '/admin/settlements/$settlementId',
                      params: { settlementId: row.id },
                    })
                  }
                />
              )}
            </StatsCard>
          </>
        )}
      </Page.Content>
    </Page>
  )
}
